/**
 * "New uploads are paused" — the chat's half of manual billing's write freeze.
 *
 * ── Why this is a component and not two inline blocks ────────────────────
 *
 * It shipped on `HeroChat` only. `ChatPanel` — the surface where most turns
 * actually happen, because the hero is only the FIRST message of a session —
 * had nothing, so a customer who started a conversation and kept going lost the
 * explanation at exactly the point they were most likely to try creating a file.
 * Two copies of one notice is how that gap opens; one component is how it stays
 * closed.
 *
 * ── Why it is localized now ──────────────────────────────────────────────
 *
 * The original carried a comment justifying a hardcoded English string: the file
 * "has no `useTranslation` and adding one for a single line would read as an
 * accident." That was not true of the package — `ModelSelector` and
 * `SessionListDialog` both use `useTranslation('agent')`, and `agui-error.ts`
 * renders the budget-exhaustion copy from the same `agent` namespace. So the
 * one thing on screen explaining why a customer's uploads stopped was the one
 * thing in the flow that only spoke English.
 *
 * ── What it must keep saying ─────────────────────────────────────────────
 *
 * Three facts, matching the 402's own copy: what stopped (new files), what
 * still works (editing what is already there, and the conversation itself), and
 * that it still spends AI credit — because a customer who reads "paused" and
 * assumes turns are free would be wrong about the bill.
 *
 * NOT a disabled composer. The freeze blocks new FILES, not conversation, and
 * plenty of turns edit existing files or write nothing at all.
 */
import { useTranslation } from '@papan/i18n'

export function UploadsPausedNotice({ className = '' }: { className?: string }) {
  const { t } = useTranslation('agent')
  return (
    <div
      role="status"
      data-testid="chat-uploads-paused"
      className={`rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 ${className}`}
    >
      <p className="text-amber-300 text-xs">{t('uploadsPaused.body')}</p>
    </div>
  )
}
